import React, { useEffect, useMemo, useState } from "react";
import { useComplianceContext } from "context/ComplianceContext";
import { useSelector, useDispatch } from "react-redux";
import { getCorporate } from "state/slices/organizations";
import ComplianceCategoryCard from "./ComplianceCategoryCard";
import ComplianceAccessGate from "./ComplianceAccessGate";
import ComplianceSummaryGrid from "./ComplianceSummaryGrid";
import { getTaskStatusBreakdown, computeSummaryFromTasks } from "./utils";
import ComplianceTemplateOverview from "./ComplianceTemplateOverview";
import ComplianceAnalysis from "./ComplianceAnalysis";
import { getAccessRequests } from "state/slices/accessRequest";
import { getFullName } from "utils";
import { Constants } from "config/constants";
import { v4 as uuidv4 } from "uuid";
import { getEmployeeAsync } from "state/slices/employee";
import { Roles_Permissions_Constants } from "config/roles-permissions";

const COMPLIANCE_VIEWS = [
  { id: "summary", label: "Compliance Summary" },
  { id: "metrics", label: "Compliance Metrics" },
  { id: "analysis", label: "Compliance Tasks" },
];

const defaultSummary = { notDue: 0, due: 0, notCompliant: 0, compliant: 0 };

const ComplianceDashboard = ({ corporateId, companyId, entityType }) => {
  const {
    corporates,
    companies,
    corporateSummaries,
    companySummaries,
    addCategory,
    addTask,
    updateTask,
    updateTaskLifecycle,
    assignResponsibility,
    updateCategoryWeight,
    STATUS_OPTIONS,
    LIFECYCLE_OPTIONS,
    FREQUENCY_OPTIONS,
    TIMELINE_TYPES,
    fetchUsersForEntity,
    getCachedUsersForEntity,
  } = useComplianceContext();

  const resolvedEntityType = useMemo(() => {
    if (entityType) return entityType;
    if (companyId) return "company";
    return "corporate";
  }, [entityType, companyId]);

  const isCompanyView = useMemo(() => {
    return entityType === "company" || Boolean(companyId);
  }, [entityType, companyId]);

  const targetId = companyId || corporateId;

  const entityCollection =
    resolvedEntityType === "company" ? companies : corporates;
  const entitySummaries =
    resolvedEntityType === "company" ? companySummaries : corporateSummaries;

  const entity = useMemo(() => {
    if (!entityCollection.length) {
      return null;
    }
    if (!targetId) {
      return entityCollection[0];
    }
    return (
      entityCollection.find((item) => item.id === targetId) ||
      entityCollection[0]
    );
  }, [entityCollection, targetId]);

  const entitySummary = useMemo(() => {
    if (!entity) {
      return { summary: defaultSummary };
    }
    return (
      entitySummaries.find((item) => item.id === entity.id) || {
        summary: defaultSummary,
      }
    );
  }, [entitySummaries, entity]);

  const { summary: initialSummary } = entitySummary || {};
  const counts = useMemo(() => {
    const categories = Array.isArray(entity?.categories)
      ? entity.categories
      : [];
    const directTasks = categories.flatMap((c) => c.tasks || []);
    const sectionTasks = categories.flatMap((c) =>
      Array.isArray(c.sections) ? c.sections.flatMap((s) => s.tasks || []) : []
    );
    const tasks = [...directTasks, ...sectionTasks];
    return getTaskStatusBreakdown(tasks);
  }, [entity?.categories]);
  const summary = useMemo(() => {
    const categories = Array.isArray(entity?.categories)
      ? entity.categories
      : [];
    const directTasks = categories.flatMap((c) => c.tasks || []);
    const sectionTasks = categories.flatMap((c) =>
      Array.isArray(c.sections) ? c.sections.flatMap((s) => s.tasks || []) : []
    );
    const tasks = [...directTasks, ...sectionTasks];
    const computed = computeSummaryFromTasks(tasks);
    const valid =
      Number(initialSummary?.compliant ?? 0) +
        Number(initialSummary?.notDue ?? 0) +
        Number(initialSummary?.due ?? 0) +
        Number(initialSummary?.notCompliant ?? 0) >
      0;
    return valid ? initialSummary : computed;
  }, [entity?.categories, initialSummary]);
  const [activeView, setActiveView] = useState(COMPLIANCE_VIEWS[0].id);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [expandedCategoryId, setExpandedCategoryId] = useState(
    entity?.categories?.[0]?.id || null
  );
  const [trackedEntityId, setTrackedEntityId] = useState(entity?.id || null);

  const analysisScope =
    resolvedEntityType === "company" ? "company" : "corporate";
  const analysisScopeId = entity?.id || null;

  const dispatch = useDispatch();
  const { companyOfficials, dashboardRoles } = useSelector(
    (state) => state.companyDashboardSlice
  );
  const { getEmployee } = useSelector((state) => state.employeeSlice);
  const { currentUser } = useSelector((state) => state.authSlice || {});

  const [primaryNestedEmployees, setPrimaryNestedEmployees] = useState([]);

  // From company officials: if an official has corporate idType, use its primarySource (corporate id)
  // to fetch the corporate and extract its employees.
  useEffect(() => {
    if (resolvedEntityType !== "company") return;
    const parentOfficials = Array.isArray(companyOfficials?.items)
      ? companyOfficials.items
      : [];
    const corporateIdTypes = new Set(
      Constants.CORPORATES_ID_TYPES.map((t) => String(t).toLowerCase())
    );
    const corporateOfficialsWithPrimary = parentOfficials.filter((official) => {
      const idTypeLc = String(official?.idType || "").toLowerCase();
      return corporateIdTypes.has(idTypeLc) && Boolean(official?.primarySource);
    });

    if (corporateOfficialsWithPrimary.length === 0) {
      setPrimaryNestedEmployees([]);
      return;
    }
    const run = async () => {
      try {
        const results = await Promise.all(
          corporateOfficialsWithPrimary.map(async (corpOfficial) => {
            const corporateIdResolved = corpOfficial?.primarySource || null;

            let corporate = null;
            let employees = [];

            if (corporateIdResolved) {
              if (currentUser?._id) {
                await dispatch(
                  getAccessRequests({ query: { createdBy: currentUser?._id } })
                );
              }
              const respDash = await dispatch(
                getCorporate({
                  corporateId: corporateIdResolved,
                  isDashboard: {
                    isDashboard: true,
                    includeDetails: true,
                    params: { suppress403Redirect: true },
                  },
                })
              );
              const payloadDash = respDash?.payload;
              corporate = payloadDash?.corporate || null;
            }

            if (!corporate && corporateIdResolved) {
              const respNonDash = await dispatch(
                getCorporate({
                  corporateId: corporateIdResolved,
                  isDashboard: {
                    includeDetails: true,
                    params: { suppress403Redirect: true },
                  },
                })
              );
              const payloadNonDash = respNonDash?.payload;
              corporate = payloadNonDash?.corporate || null;
            }

            if (corporate) {
              employees =
                corporate?.employees ||
                corporate?.organizationEmployees ||
                corporate?.companyEmployees ||
                [];
              if (!Array.isArray(employees)) return [];
              const corpName =
                corporate?.names?.companyName ||
                corporate?.name ||
                corporate?.legalName ||
                corporate?.registrationNumber ||
                corporate?.id ||
                corporate?._id ||
                "Corporate";
              return employees.map((emp) => ({
                ...emp,
                _sourceCorporateName: corpName,
                _sourceCorporateId: corporate?.id || corporate?._id || "",
              }));
            }
            return [];
          })
        );
        const flatEmployees = results.flat().filter(Boolean);
        setPrimaryNestedEmployees(flatEmployees);
      } catch (e) {
        console.error("Failed to fetch corporate by primarySource:", e);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedEntityType, companyOfficials?.items]);

  // Derive categories to display:
  // - For companies: show categories from associated org admin's assigned template (via corporates)
  //   If no org admin or no assigned template, show empty categories
  // - For corporates: show the corporate's own categories
  const normalizeId = (v) => (v ? String(v) : "");
  const orgAdminCorporate = useMemo(() => {
    if (resolvedEntityType !== "company") return null;
    const orgAdminId = normalizeId(entity?.orgAdminId);
    const byId =
      orgAdminId &&
      (corporates.find(
        (c) =>
          normalizeId(c?.id) === orgAdminId ||
          normalizeId(c?._id) === orgAdminId
      ) ||
        null);
    if (byId) return byId;
    const compId = normalizeId(entity?.id);
    if (!compId) return null;
    const byAssociation =
      corporates.find((c) => {
        const ids = Array.isArray(c?.associatedCompanyIds)
          ? c.associatedCompanyIds.map(normalizeId)
          : [];
        if (ids.includes(compId)) return true;
        const companies = Array.isArray(c?.associatedCompanies)
          ? c.associatedCompanies
          : [];
        return companies.some(
          (co) =>
            normalizeId(co?.id) === compId || normalizeId(co?._id) === compId
        );
      }) || null;
    return byAssociation;
  }, [resolvedEntityType, entity?.orgAdminId, entity?.id, corporates]);

  const hasApproverRole = useMemo(() => {
    const roles = Array.isArray(dashboardRoles) ? dashboardRoles : [];
    const allowedRoles = [
  Roles_Permissions_Constants.DASHBOARD_ROLES.APPROVER,
  Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR
];

return roles.some((r) =>
  allowedRoles.includes(String(r?.name).toUpperCase())
);
  }, [dashboardRoles]);

  const displayCategories = useMemo(() => {
    if (resolvedEntityType === "company") {
      const selfCats = Array.isArray(entity?.categories)
        ? entity.categories
        : [];
      if (selfCats.length > 0) return selfCats;
      const orgCats = Array.isArray(orgAdminCorporate?.categories)
        ? orgAdminCorporate.categories
        : [];
      if (orgCats.length > 0) return orgCats;
      return [];
    }
    // Corporate view uses its own categories
    return entity?.categories || [];
  }, [resolvedEntityType, entity?.categories, orgAdminCorporate]);

  const hasTemplateAssigned = useMemo(() => {
    if (!entity) return false;
    if (resolvedEntityType === "company") {
      const selfTemplate =
        entity.template &&
        (entity.template.templateId ||
          entity.template.id ||
          entity.template._id);
      const orgTemplate =
        orgAdminCorporate &&
        orgAdminCorporate.template &&
        (orgAdminCorporate.template.templateId ||
          orgAdminCorporate.template.id ||
          orgAdminCorporate.template._id);
      return Boolean(selfTemplate || orgTemplate);
    }
    const corporateTemplate =
      entity.template &&
      (entity.template.templateId || entity.template.id || entity.template._id);
    return Boolean(corporateTemplate);
  }, [entity, resolvedEntityType, orgAdminCorporate]);

  // Fetch users in useEffect to avoid setState during render
  useEffect(() => {
    if (!entity?.id) return;

    // Trigger async fetch (this will update cache asynchronously)
    // This is safe because useEffect runs after render
    fetchUsersForEntity(entity.id, resolvedEntityType);
  }, [entity?.id, resolvedEntityType, fetchUsersForEntity]);

  // Return cached users synchronously (no side effects, just reads from cache)
  // Use getCachedUsersForEntity to avoid triggering setState during render
  const availableUsers = useMemo(() => {
    if (!entity?.id) return [];
    if (resolvedEntityType === "company") {
      const officials = Array.isArray(companyOfficials?.items)
        ? companyOfficials.items
        : [];
      // Split into direct-person officials vs corporate-linked officials
      const corporateIdTypes = new Set(
        Constants.CORPORATES_ID_TYPES.map((t) => String(t).toLowerCase())
      );
      const directOfficials = [];
      const linkedCompanyOfficials = [];
      officials.forEach((official) => {
        const idTypeLc = String(official?.idType || "").toLowerCase();
        if (corporateIdTypes.has(idTypeLc)) {
          linkedCompanyOfficials.push(official);
        } else {
          directOfficials.push(official);
        }
      });
      const filtered = directOfficials.filter((official) => {
        const roles = Array.isArray(official?.role) ? official.role : [];
        const designation = String(official?.designation || "");
        const hasExcludedRole = roles.some((r) => {
          const val = String(r || "").toLowerCase();
          return (
            val === "shareholder" ||
            val === "jointshareholder" ||
            val === "joint shareholder"
          );
        });
        const isExcludedDesignation = [
          "shareholder",
          "jointshareholder",
          "joint shareholder",
        ].includes(designation.toLowerCase());
        return !(hasExcludedRole || isExcludedDesignation);
      });
      const officialUsers = filtered.map((official) => {
        const id =
          official?.user?._id ||
          official?._id ||
          official?.id ||
          official?.individualId ||
          official?.beneficialOwnerId ||
          String(Math.random());
        const name =
          getFullName(official?.names) ||
          official?.electronicAddress?.emailAddress ||
          "Company Official";
        const title =
          (Array.isArray(official?.role) && official.role.join(", ")) ||
          official?.designation ||
          "Official";
        const department = entity?.companyName
          ? `Company Officials(${entity.companyName})`
          : entity?.name
          ? `Company Officials(${entity.name})`
          : "Company Officials";
        const initials = name
          .split(" ")
          .filter(Boolean)
          .map((p) => p.charAt(0).toUpperCase())
          .slice(0, 2)
          .join("");
        return {
          id: String(id),
          name,
          email: official?.electronicAddress?.emailAddress || "",
          title,
          department,
          initials,
          source: "companyOfficials",
        };
      });
      // Flatten users from linked-company officials: employees + corporateOfficials
      const linkedUsersFromOfficials = [];
      linkedCompanyOfficials.forEach((corpOfficial) => {
        const lEmployees = Array.isArray(corpOfficial?.employees)
          ? corpOfficial.employees
          : [];
        const lCorpOfficials = Array.isArray(corpOfficial?.corporateOfficials)
          ? corpOfficial.corporateOfficials
          : [];
        // Map employees
        lEmployees.forEach((emp) => {
          const uid = emp?.user?._id || emp?._id || emp?.id || uuidv4();
          const fullName =
            getFullName(emp?.user?.names) ||
            emp?.user?.electronicAddress?.emailAddress ||
            emp?.user?.email ||
            "Employee";
          const initials = fullName
            .split(" ")
            .filter(Boolean)
            .map((p) => p.charAt(0).toUpperCase())
            .slice(0, 2)
            .join("");
          linkedUsersFromOfficials.push({
            id: String(uid),
            name: fullName,
            email:
              emp?.user?.electronicAddress?.emailAddress ||
              emp?.user?.email ||
              "",
            title: "Employee",
            department: "Company Employee",
            initials,
            source: "linkedCompanyEmployees",
          });
        });
        // Map corporate officials, excluding shareholders/joint shareholders
        lCorpOfficials
          .filter((official) => {
            const roles = Array.isArray(official?.role) ? official.role : [];
            const designation = String(official?.designation || "");
            const hasExcludedRole = roles.some((r) => {
              const val = String(r || "").toLowerCase();
              return (
                val === "shareholder" ||
                val === "jointshareholder" ||
                val === "joint shareholder"
              );
            });
            const isExcludedDesignation = [
              "shareholder",
              "jointshareholder",
              "joint shareholder",
            ].includes(designation.toLowerCase());
            return !(hasExcludedRole || isExcludedDesignation);
          })
          .forEach((official) => {
            const id =
              official?.user?._id ||
              official?._id ||
              official?.id ||
              official?.individualId ||
              official?.beneficialOwnerId ||
              String(Math.random());
            const name =
              getFullName(official?.names) ||
              official?.electronicAddress?.emailAddress ||
              "Company Official";
            const title =
              (Array.isArray(official?.role) && official.role.join(", ")) ||
              official?.designation ||
              "Official";
            const department = "Company Officials";
            const initials = name
              .split(" ")
              .filter(Boolean)
              .map((p) => p.charAt(0).toUpperCase())
              .slice(0, 2)
              .join("");
            linkedUsersFromOfficials.push({
              id: String(id),
              name,
              email: official?.electronicAddress?.emailAddress || "",
              title,
              department,
              initials,
              source: "linkedCompanyOfficials",
            });
          });
      });
      // Include company employees
      const employees = Array.isArray(getEmployee?.items)
        ? getEmployee.items
        : [];
      const employeeUsers = employees.map((emp) => {
        const uid = emp?.user?._id || emp?._id || emp?.id || uuidv4();
        const fullName =
          getFullName(emp?.user?.names) ||
          emp?.user?.electronicAddress?.emailAddress ||
          emp?.user?.email ||
          "Employee";
        const initials = fullName
          .split(" ")
          .filter(Boolean)
          .map((p) => p.charAt(0).toUpperCase())
          .slice(0, 2)
          .join("");
        return {
          id: String(uid),
          name: fullName,
          email:
            emp?.user?.electronicAddress?.emailAddress ||
            emp?.user?.email ||
            "",
          title: "Employee",
          department: entity?.companyName
            ? `Employee(${entity.companyName})`
            : entity?.name
            ? `Employee(${entity.name})`
            : "Employee",
          initials,
          source: "employees",
        };
      });
      // Employees fetched from primarySource corporate lookups
      const primaryEmployeesUsers = Array.isArray(primaryNestedEmployees)
        ? primaryNestedEmployees.map((emp) => {
            const uid = emp?.user?._id || emp?._id || emp?.id || uuidv4();
            const fullName =
              getFullName(emp?.user?.names) ||
              getFullName(emp?.names) ||
              emp?.user?.electronicAddress?.emailAddress ||
              emp?.email ||
              emp?.user?.email ||
              "Employee";
            const emailAddr =
              emp?.user?.electronicAddress?.emailAddress ||
              emp?.email ||
              emp?.user?.email ||
              "";
            const initials = fullName
              .split(" ")
              .filter(Boolean)
              .map((p) => p.charAt(0).toUpperCase())
              .slice(0, 2)
              .join("");
            return {
              id: String(uid),
              name: fullName,
              email: emailAddr,
              title: "Employee",
              department: `Employee(${
                emp?._sourceCorporateName || "Corporate"
              })`,
              initials,
              source: "primarySourceEmployees",
            };
          })
        : [];

      // Merge and de-duplicate by id preference: linked → primaryEmployees → employees → officials
      const byId = new Map();
      linkedUsersFromOfficials.forEach((u) => byId.set(u.id, u));
      primaryEmployeesUsers.forEach((u) => byId.set(u.id, u));
      employeeUsers.forEach((u) => byId.set(u.id, u));
      officialUsers.forEach((u) => byId.set(u.id, u));
      return Array.from(byId.values());
    }
    return getCachedUsersForEntity(entity.id, resolvedEntityType);
  }, [
    entity?.id,
    entity?.companyName,
    entity?.name,
    resolvedEntityType,
    getCachedUsersForEntity,
    companyOfficials?.items,
    getEmployee?.items,
    primaryNestedEmployees,
  ]);

  // Ensure company employees are fetched asynchronously like other candidates
  useEffect(() => {
    if (resolvedEntityType !== "company") return;
    if (!entity?.id) return;
    dispatch(getEmployeeAsync({ companyId: entity.id }));
  }, [resolvedEntityType, entity?.id, dispatch]);

  const statusEditableIds = useMemo(() => {
    const ids = new Set();
    const currentId =
      currentUser?._id || currentUser?.id || currentUser?.user?._id || null;
    if (currentId) ids.add(String(currentId));
    const currentEmail =
      currentUser?.electronicAddress?.emailAddress || currentUser?.email || "";
    if (currentEmail) {
      const emailLc = String(currentEmail).toLowerCase();
      (availableUsers || []).forEach((u) => {
        const uEmail = String(u?.email || "").toLowerCase();
        if (uEmail && uEmail === emailLc && u?.id) {
          ids.add(String(u.id));
        }
      });
    }
    return Array.from(ids);
  }, [
    currentUser?._id,
    currentUser?.id,
    currentUser?.electronicAddress?.emailAddress,
    currentUser?.email,
    availableUsers,
  ]);

  useEffect(() => {
    if (!entity) {
      setExpandedCategoryId(null);
      setTrackedEntityId(null);
      return;
    }

    setExpandedCategoryId((current) => {
      if (entity.id !== trackedEntityId) {
        setTrackedEntityId(entity.id);
        // When viewing a company, default to the first category derived from org admin
        return (displayCategories && displayCategories[0]?.id) || null;
      }

      if (!current) {
        return current;
      }

      const exists = (displayCategories || []).some(
        (category) => category.id === current
      );

      if (exists) {
        return current;
      }

      return (displayCategories && displayCategories[0]?.id) || null;
    });
  }, [entity, trackedEntityId, displayCategories]);

  const handleAddCategory = (event) => {
    event.preventDefault();
    if (!entity || !newCategoryName) return;
    addCategory(entity.id, newCategoryName, resolvedEntityType);
    setNewCategoryName("");
    setIsAddingCategory(false);
  };

  const handleAddTask = (categoryId, taskPayload) => {
    if (!entity) return;
    addTask(
      entity.id,
      categoryId,
      taskPayload,
      resolvedEntityType,
      displayCategories
    );
  };

  const handleUpdateTask = (categoryId, taskId, updates) => {
    if (!entity) return;
    updateTask(
      entity.id,
      categoryId,
      taskId,
      updates,
      resolvedEntityType,
      displayCategories
    );
  };

  const handleLifecycleChange = (categoryId, taskId, lifecycleStatus) => {
    if (!entity) return;
    updateTaskLifecycle(
      entity.id,
      categoryId,
      taskId,
      lifecycleStatus,
      resolvedEntityType,
      displayCategories
    );
  };

  const handleAssignResponsibility = (categoryId, taskId, responsibilities) => {
    if (!entity) return;
    assignResponsibility(
      entity.id,
      categoryId,
      taskId,
      responsibilities,
      resolvedEntityType,
      displayCategories
    );
  };

  const handleWeightChange = (categoryId, weight) => {
    if (!entity) return;
    updateCategoryWeight(
      entity.id,
      categoryId,
      weight,
      resolvedEntityType,
      displayCategories
    );
  };
  const currentYear = entity?.year || new Date().getFullYear();
  const entityLabel =
    resolvedEntityType === "company" ? "Company" : "Corporate";

  return (
    <ComplianceAccessGate>
      <div className="space-y-8">
        <div className="space-y-4 border-b border-gray-200 pb-6">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.2rem] text-gray-500">
              Compliance Tracker
            </p>
            <h2 className="text-3xl font-bold text-gray-900">
              Compliance Tracker for {currentYear}
            </h2>
            {entity?.name && (
              <p className="text-sm text-gray-600">
                {entityLabel}:{" "}
                <span className="font-semibold text-gray-800">
                  {entity.name}
                </span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {COMPLIANCE_VIEWS.map((view) => {
              const isActive = activeView === view.id;
              return (
                <button
                  key={view.id}
                  type="button"
                  onClick={() => setActiveView(view.id)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "border-sflPrimary bg-sflPrimary text-white shadow"
                      : "border-gray-200 bg-white text-gray-600 hover:border-sflPrimary/40 hover:text-sflPrimary"
                  }`}
                >
                  {view.label}
                </button>
              );
            })}
          </div>
        </div>

        {activeView === "summary" && (
          <div className="space-y-6">
            <ComplianceSummaryGrid
              summary={summary}
              counts={counts}
              caption="Percentages are computed from active tasks only"
            />
            <ComplianceTemplateOverview categories={displayCategories} />
          </div>
        )}

        {activeView === "metrics" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">
                Compliance Categories
              </h3>
              {hasTemplateAssigned && !isAddingCategory && (
                <button
                  type="button"
                  onClick={() => setIsAddingCategory(true)}
                  className="text-sm font-semibold text-sflPrimary hover:underline"
                >
                  + Add Category
                </button>
              )}
            </div>
            {resolvedEntityType === "company" && !hasTemplateAssigned && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                No template assigned to this company. Please assign a compliance
                template to manage company tasks and deadlines.
              </div>
            )}
            {isAddingCategory && (
              <form
                onSubmit={handleAddCategory}
                className="flex flex-col gap-3 rounded-xl border border-dashed border-sflPrimary/40 bg-slate-50 p-4 md:flex-row md:items-end"
              >
                <div className="flex-1">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Category Name
                  </label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(event) => setNewCategoryName(event.target.value)}
                    required
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setNewCategoryName("");
                      setIsAddingCategory(false);
                    }}
                    className="rounded-md border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-md bg-sflPrimary px-4 py-2 text-xs font-semibold text-white"
                  >
                    Save Category
                  </button>
                </div>
              </form>
            )}
            <div className="space-y-6">
              {(displayCategories || []).map((category) => (
                <ComplianceCategoryCard
                  key={category.id}
                  corporateId={
                    isCompanyView
                      ? orgAdminCorporate?.id || entity?.id
                      : entity?.id
                  }
                  category={category}
                  statusOptions={STATUS_OPTIONS}
                  lifecycleOptions={LIFECYCLE_OPTIONS}
                  frequencyOptions={FREQUENCY_OPTIONS}
                  timelineTypes={TIMELINE_TYPES}
                  isCompanyView={isCompanyView}
                  availableUsers={availableUsers}
                  canEditScheduling={hasApproverRole}
                  canAssignResponsibility={hasApproverRole}
                  canEditLifecycle={hasApproverRole}
                  checklistHeaderEditable={false}
                  statusEditableIds={statusEditableIds}
                  onAddTask={handleAddTask}
                  onUpdateTask={handleUpdateTask}
                  onLifecycleChange={handleLifecycleChange}
                  onAssignResponsibility={handleAssignResponsibility}
                  onWeightChange={handleWeightChange}
                  isOpen={expandedCategoryId === category.id}
                  onToggle={() =>
                    setExpandedCategoryId((current) =>
                      current === category.id ? null : category.id
                    )
                  }
                />
              ))}
            </div>
          </div>
        )}

        {activeView === "analysis" && (
          <ComplianceAnalysis scope={analysisScope} scopeId={analysisScopeId} />
        )}
      </div>
    </ComplianceAccessGate>
  );
};

export default ComplianceDashboard;
